/**
 * ProductTable — sortable, groupable list view with drag-and-drop column reordering.
 *
 * Features:
 * - 27 columns (core + financial + timeline + resource)
 * - Sort by any column (click header)
 * - Group by Initiative or Axis (collapsible groups)
 * - Inline editing: status, phase, priority, ring, TAM, SAM
 * - Drag-and-drop column reordering (persisted in localStorage)
 * - Sticky Name column for horizontal scrolling
 * - Single-click selects, double-click opens NodeModal
 * - Delete column pinned at end, not draggable
 */

import { useState, useMemo, useCallback, useRef, type ReactNode } from 'react';
import {
  ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown,
  Search, Plus, Trash2, Rows3, Sparkles, Check, X,
} from 'lucide-react';
import { useAdjacencyStore } from '../../store/adjacency-store';
import type {
  AdjacencyNode, Status, Phase, RelatednessRing,
  StrategicInitiative, AdjacencyAxis,
} from '../../types/adjacency';
import { RING_SUCCESS_RATES } from '../../types/adjacency';
import { INITIATIVE_COLORS, STATUS_COLORS, STATUS_DEFAULT_COLOR } from '../../utils/map-colors';
import { AXIS_COLORS, PHASE_COLORS } from '../../utils/view-colors';
import { getAllPhases, getAllStatuses } from '../../utils/reference-data';
import { nodePassesFilter } from '../../utils/filter-nodes';
import { NodeModal } from '../editors/NodeModal';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { BulkEditPanel } from './BulkEditPanel';
import { useFteAllocations } from '../../hooks/useFteAllocations';

// ── Types ────────────────────────────────────────────────────────────

type SortKey =
  | 'name' | 'description' | 'initiative' | 'axis' | 'ring' | 'phase'
  | 'status' | 'priority' | 'parents' | 'children' | 'related' | 'bain'
  | 'iRnD' | 'fRnD' | 'totalInv' | 'estRevenue' | 'estRnD'
  | 'fteAllocation' | 'tam' | 'sam'
  | 'startDate' | 'atdDate' | 'atqDate' | 'atsDate' | 'mrdDate'
  | 'devLane' | 'productManager';

type SortDir = 'asc' | 'desc' | null;
type GroupBy = 'none' | 'initiative' | 'axis';

interface ColumnDef {
  key: string;
  label: string;
  sortKey: SortKey;
  align?: 'left' | 'center' | 'right';
}

const ALL_RINGS: RelatednessRing[] = [0, 1, 2, 3, 4];
const STORAGE_KEY_COL_ORDER = 'iwt-column-order';

// ── Formatting helpers ──────────────────────────────────────────────

function fmtCurrency(val: number | null): string {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
}

// ── Relationship helpers ────────────────────────────────────────────

function getRelatedNodes(
  node: AdjacencyNode,
  relationship: 'parent' | 'child' | 'peer',
  allNodes: AdjacencyNode[],
): AdjacencyNode[] {
  const ids = new Set<string>();
  for (const edge of node.edges) {
    if (edge.relationship === relationship) ids.add(edge.targetId);
  }
  const reverseType = relationship === 'parent' ? 'child' : relationship === 'child' ? 'parent' : 'peer';
  for (const other of allNodes) {
    if (other.id === node.id) continue;
    for (const edge of other.edges) {
      if (edge.targetId === node.id && edge.relationship === reverseType) ids.add(other.id);
    }
  }
  const nodeMap = new Map(allNodes.map((n) => [n.id, n]));
  return [...ids].map((id) => nodeMap.get(id)).filter(Boolean) as AdjacencyNode[];
}

function getRelCount(node: AdjacencyNode, rel: 'parent' | 'child' | 'peer', allNodes: AdjacencyNode[]): number {
  return getRelatedNodes(node, rel, allNodes).length;
}

// ── Sort value extraction ───────────────────────────────────────────

function compareSortKey(
  a: AdjacencyNode, b: AdjacencyNode, key: SortKey, allNodes: AdjacencyNode[],
): number {
  const va = getSortVal(a, key, allNodes);
  const vb = getSortVal(b, key, allNodes);
  if (va === vb) return 0;
  if (va == null) return 1;
  if (vb == null) return -1;
  if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb);
  return (va as number) < (vb as number) ? -1 : 1;
}

function getSortVal(n: AdjacencyNode, key: SortKey, allNodes: AdjacencyNode[]): string | number | null {
  switch (key) {
    case 'name': return n.name.toLowerCase();
    case 'description': return n.description.toLowerCase();
    case 'initiative': return n.strategicInitiative;
    case 'axis': return n.adjacencyAxis;
    case 'ring': return n.relatednessRing;
    case 'phase': return n.phase ?? null;
    case 'status': return n.status ?? null;
    case 'priority': return n.priority ?? null;
    case 'parents': return getRelCount(n, 'parent', allNodes);
    case 'children': return getRelCount(n, 'child', allNodes);
    case 'related': return getRelCount(n, 'peer', allNodes);
    case 'bain': return n.attractivenessScore;
    case 'iRnD': return n.iRnD;
    case 'fRnD': return n.fRnD;
    case 'totalInv': { const t = (n.iRnD ?? 0) + (n.fRnD ?? 0); return t > 0 ? t : null; }
    case 'estRevenue': return n.estRevenue;
    case 'estRnD': return n.estRnD;
    case 'fteAllocation': return n.fteAllocation;  // Sort uses stored value; display uses reactive
    case 'tam': return n.tam;
    case 'sam': return n.sam;
    case 'startDate': return n.startDate;
    case 'atdDate': return n.atdDate;
    case 'atqDate': return n.atqDate;
    case 'atsDate': return n.atsDate;
    case 'mrdDate': return n.mrdDate;
    case 'devLane': return n.devLane;
    case 'productManager': return n.productManager;
  }
}

// ── Column definitions (default order) ──────────────────────────────

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Name', sortKey: 'name' },
  { key: 'initiative', label: 'Initiative', sortKey: 'initiative' },
  { key: 'axis', label: 'Axis', sortKey: 'axis' },
  { key: 'ring', label: 'Ring', sortKey: 'ring' },
  { key: 'phase', label: 'Phase', sortKey: 'phase' },
  { key: 'status', label: 'Status', sortKey: 'status' },
  { key: 'priority', label: 'Priority', sortKey: 'priority', align: 'center' },
  { key: 'parents', label: 'Parents', sortKey: 'parents' },
  { key: 'children', label: 'Children', sortKey: 'children' },
  { key: 'related', label: 'Related', sortKey: 'related' },
  { key: 'bain', label: 'Bain', sortKey: 'bain', align: 'center' },
  // New financial columns
  { key: 'description', label: 'Description', sortKey: 'description' },
  { key: 'iRnD', label: 'iR&D', sortKey: 'iRnD', align: 'right' },
  { key: 'fRnD', label: 'FR&D', sortKey: 'fRnD', align: 'right' },
  { key: 'totalInv', label: 'Total Inv.', sortKey: 'totalInv', align: 'right' },
  { key: 'estRevenue', label: 'Est Revenue', sortKey: 'estRevenue', align: 'right' },
  { key: 'estRnD', label: 'Est R&D SP (Ignored)', sortKey: 'estRnD', align: 'right' },
  { key: 'fteAllocation', label: 'FTEs', sortKey: 'fteAllocation', align: 'right' },
  { key: 'tam', label: 'TAM', sortKey: 'tam', align: 'right' },
  { key: 'sam', label: 'SAM', sortKey: 'sam', align: 'right' },
  // Timeline columns
  { key: 'startDate', label: 'Start Date', sortKey: 'startDate', align: 'center' },
  { key: 'atdDate', label: 'ATD', sortKey: 'atdDate', align: 'center' },
  { key: 'atqDate', label: 'ATQ', sortKey: 'atqDate', align: 'center' },
  { key: 'atsDate', label: 'ATS', sortKey: 'atsDate', align: 'center' },
  { key: 'mrdDate', label: 'MRD', sortKey: 'mrdDate', align: 'center' },
  // Informational
  { key: 'devLane', label: 'Dev Lane', sortKey: 'devLane' },
  { key: 'productManager', label: 'PM', sortKey: 'productManager' },
];

const DEFAULT_COL_KEYS = ALL_COLUMNS.map((c) => c.key);
const COL_MAP = new Map(ALL_COLUMNS.map((c) => [c.key, c]));

function loadColumnOrder(): string[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_COL_ORDER);
    if (saved) {
      const parsed = JSON.parse(saved) as string[];
      const knownKeys = new Set(DEFAULT_COL_KEYS);
      const valid = parsed.filter((k) => knownKeys.has(k));
      const missing = DEFAULT_COL_KEYS.filter((k) => !valid.includes(k));
      return [...valid, ...missing];
    }
  } catch { /* ignore */ }
  return [...DEFAULT_COL_KEYS];
}

// ── Component ────────────────────────────────────────────────────────

export function ProductTable() {
  const {
    nodes, filters, setFilters, selectedNodeId, selectNode, updateNode,
    deleteNode, duplicateNode, createNode, replaceAllNodes,
    createParentChildLink, createRelatedLink, removeRelationship, convertEdgeType,
  } = useAdjacencyStore();

  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [expandedRelCells, setExpandedRelCells] = useState<Set<string>>(new Set());
  const [editingStatusId, setEditingStatusId] = useState<string | null>(null);
  const [editingPhaseId, setEditingPhaseId] = useState<string | null>(null);
  const [editingPriorityId, setEditingPriorityId] = useState<string | null>(null);
  const [editingRingId, setEditingRingId] = useState<string | null>(null);
  const [editingTamSam, setEditingTamSam] = useState<{ nodeId: string; field: 'tam' | 'sam' } | null>(null);
  const [editTamSamValue, setEditTamSamValue] = useState('');
  const [detailNodeId, setDetailNodeId] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string; message: string; anchorY?: number; onConfirm: () => void;
  } | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Column reorder state
  const [columnOrder, setColumnOrder] = useState<string[]>(loadColumnOrder);
  const [draggingCol, setDraggingCol] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);

  const fteAllocations = useFteAllocations();

  const orderedColumns = useMemo(() => {
    return columnOrder.map((key) => COL_MAP.get(key)).filter(Boolean) as ColumnDef[];
  }, [columnOrder]);

  // Filter nodes
  const filteredNodes = useMemo(
    () => nodes.filter((n) => nodePassesFilter(n, filters)),
    [nodes, filters],
  );

  // Dynamic Phase/Status options
  const phaseOptions = useMemo(() => getAllPhases(nodes), [nodes]);
  const statusOptions = useMemo(() => getAllStatuses(nodes), [nodes]);

  // Sort
  const sortedNodes = useMemo(() => {
    if (!sortKey || !sortDir) return filteredNodes;
    const sorted = [...filteredNodes].sort((a, b) => compareSortKey(a, b, sortKey, nodes));
    return sortDir === 'desc' ? sorted.reverse() : sorted;
  }, [filteredNodes, sortKey, sortDir, nodes]);

  // Group
  const groups = useMemo(() => {
    if (groupBy === 'none') return [{ key: '__all__', label: '', nodes: sortedNodes }];
    const map = new Map<string, AdjacencyNode[]>();
    for (const node of sortedNodes) {
      const gKey = groupBy === 'initiative' ? node.strategicInitiative : node.adjacencyAxis;
      if (!map.has(gKey)) map.set(gKey, []);
      map.get(gKey)!.push(node);
    }
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, n]) => ({ key, label: key, nodes: n }));
  }, [sortedNodes, groupBy]);

  // Total column count for colSpan
  const totalColSpan = orderedColumns.length + 1; // +1 for delete column

  // ── Handlers ──────────────────────────────────────────────────────

  const handleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc');
      else if (sortDir === 'desc') { setSortKey(null); setSortDir(null); }
      else setSortDir('asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }, [sortKey, sortDir]);

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const toggleRelCell = useCallback((cellKey: string) => {
    setExpandedRelCells((prev) => {
      const next = new Set(prev);
      if (next.has(cellKey)) next.delete(cellKey); else next.add(cellKey);
      return next;
    });
  }, []);

  const handleStatusChange = useCallback((nodeId: string, newStatus: Status) => {
    void updateNode(nodeId, { status: newStatus });
    setEditingStatusId(null);
  }, [updateNode]);

  const handlePhaseChange = useCallback((nodeId: string, newPhase: Phase | null) => {
    void updateNode(nodeId, { phase: newPhase });
    setEditingPhaseId(null);
  }, [updateNode]);

  const handlePriorityBlur = useCallback((nodeId: string, value: string) => {
    const num = value.trim() === '' ? null : Number(value);
    void updateNode(nodeId, { priority: num != null && !isNaN(num) ? num : null });
    setEditingPriorityId(null);
  }, [updateNode]);

  const handleRingChange = useCallback((nodeId: string, newRing: RelatednessRing) => {
    void updateNode(nodeId, { relatednessRing: newRing });
    setEditingRingId(null);
  }, [updateNode]);

  const startTamSamEdit = (nodeId: string, field: 'tam' | 'sam', currentValue: number | null) => {
    setEditingTamSam({ nodeId, field });
    setEditTamSamValue(currentValue != null ? String(currentValue) : '');
  };

  const commitTamSamEdit = () => {
    if (!editingTamSam) return;
    const num = parseFloat(editTamSamValue);
    void updateNode(editingTamSam.nodeId, { [editingTamSam.field]: isNaN(num) ? null : num });
    setEditingTamSam(null);
  };

  const cancelTamSamEdit = () => setEditingTamSam(null);

  const handleRowClick = useCallback((nodeId: string) => selectNode(nodeId), [selectNode]);
  const handleRowDoubleClick = useCallback((nodeId: string) => {
    selectNode(nodeId);
    setDetailNodeId(nodeId);
  }, [selectNode]);

  const handleAddNode = useCallback(async () => {
    const newNode = await createNode({
      name: 'New Node', description: '', strategicInitiative: 'Other',
      phase: null, status: 'Not Started', priority: null,
      adjacencyAxis: 'New Services', relatednessRing: 1,
      sharedCustomers: 5, sharedCosts: 5, commonCompetitors: 5,
      attractivenessScore: 5, reinforcesCore: false, nodeType: 'product',
      source: 'manual', edges: [], parentIds: [], mapPosition: { x: 0, y: -0.1 },
      spPriorityTag: null, spSyncReviewStatus: null, spListItemId: null, lastSyncedAt: null,
      estRnD: null, estRevenue: null, iRnD: null, fRnD: null, totalBudgetEstimate: null,
      tam: null, sam: null, tamAiSuggested: null, samAiSuggested: null,
      startDate: null, endDate: null, mrdDate: null, mrdTargetDate: null, atdDate: null, atqDate: null, atsDate: null,
      ganttStartDate: null, ganttEndDate: null, fteAllocation: null, iwtSouth: false, iwtSouthFte: null,
      isMvp: false, devLane: null, devTeam: null, productManager: null, productLine: null, budgetProjectName: null,
      spSyncSnapshot: null,
    });
    selectNode(newNode.id);
    setDetailNodeId(newNode.id);
  }, [createNode, selectNode]);

  const handleDeleteRow = useCallback((nodeId: string, nodeName: string, clickY?: number) => {
    let anchorY: number | undefined;
    if (clickY != null && tableContainerRef.current) {
      const r = tableContainerRef.current.getBoundingClientRect();
      anchorY = clickY - r.top;
    }
    setPendingConfirm({
      title: 'Delete Node', message: `Delete "${nodeName}"? You can undo this with Ctrl+Z.`, anchorY,
      onConfirm: () => { void deleteNode(nodeId); setPendingConfirm(null); },
    });
  }, [deleteNode]);

  // ── Drag-and-drop column reorder ──────────────────────────────────

  const handleDragStart = useCallback((e: React.DragEvent, colKey: string) => {
    setDraggingCol(colKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', colKey);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, colKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(colKey);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetKey: string) => {
    e.preventDefault();
    const sourceKey = e.dataTransfer.getData('text/plain');
    if (!sourceKey || sourceKey === targetKey) {
      setDraggingCol(null);
      setDragOverCol(null);
      return;
    }
    setColumnOrder((prev) => {
      const next = prev.filter((k) => k !== sourceKey);
      const targetIdx = next.indexOf(targetKey);
      next.splice(targetIdx, 0, sourceKey);
      localStorage.setItem(STORAGE_KEY_COL_ORDER, JSON.stringify(next));
      return next;
    });
    setDraggingCol(null);
    setDragOverCol(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingCol(null);
    setDragOverCol(null);
  }, []);

  // ── Cell renderers ────────────────────────────────────────────────

  const groupColor = (key: string): string => {
    if (groupBy === 'initiative') return INITIATIVE_COLORS[key as StrategicInitiative] ?? '#6b7280';
    if (groupBy === 'axis') return AXIS_COLORS[key as AdjacencyAxis] ?? '#6b7280';
    return '#6b7280';
  };

  function renderCell(col: ColumnDef, node: AdjacencyNode): ReactNode {
    const key = col.key;
    switch (key) {
      case 'name':
        return (
          <span className="font-medium truncate block max-w-[180px]" style={{ color: 'var(--text-primary)' }}>
            {node.name}
          </span>
        );
      case 'description':
        return (
          <span className="truncate block max-w-[160px]" title={node.description}>
            {node.description || <Dim>—</Dim>}
          </span>
        );
      case 'initiative':
        return <Badge label={node.strategicInitiative} color={INITIATIVE_COLORS[node.strategicInitiative]} />;
      case 'axis':
        return <Badge label={node.adjacencyAxis === 'New Technologies & Capabilities' ? 'New Tech' : node.adjacencyAxis} color={AXIS_COLORS[node.adjacencyAxis]} />;
      case 'ring':
        return editingRingId === node.id ? (
          <span onClick={(e) => e.stopPropagation()}>
            <select autoFocus value={node.relatednessRing}
              onChange={(e) => handleRingChange(node.id, Number(e.target.value) as RelatednessRing)}
              onBlur={() => setEditingRingId(null)}
              className="text-xs rounded px-1 py-0.5 border-0 outline-none"
              style={{ background: 'var(--glass-bg-elevated)', color: 'var(--text-primary)' }}
            >
              {ALL_RINGS.map((r) => <option key={r} value={r}>{r} — {RING_SUCCESS_RATES[r].label}</option>)}
            </select>
          </span>
        ) : (
          <span onClick={(e) => { e.stopPropagation(); setEditingRingId(node.id); }} className="cursor-pointer" title="Click to edit ring">
            {node.relatednessRing} — {RING_SUCCESS_RATES[node.relatednessRing].label}
          </span>
        );
      case 'phase':
        return editingPhaseId === node.id ? (
          <span onClick={(e) => e.stopPropagation()}>
            <select autoFocus value={node.phase ?? ''}
              onChange={(e) => handlePhaseChange(node.id, (e.target.value || null) as Phase | null)}
              onBlur={() => setEditingPhaseId(null)}
              className="text-xs rounded px-1 py-0.5 border-0 outline-none"
              style={{ background: 'var(--glass-bg-elevated)', color: 'var(--text-primary)' }}
            >
              <option value="">—</option>
              {phaseOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </span>
        ) : (
          <span onClick={(e) => { e.stopPropagation(); setEditingPhaseId(node.id); }} className="cursor-pointer" title="Click to edit phase">
            {node.phase ? <Badge label={node.phase} color={PHASE_COLORS[node.phase] ?? '#6b7280'} /> : <Dim>—</Dim>}
          </span>
        );
      case 'status': {
        const sColor = node.status ? (STATUS_COLORS[node.status] ?? STATUS_DEFAULT_COLOR) : STATUS_DEFAULT_COLOR;
        return editingStatusId === node.id ? (
          <span onClick={(e) => e.stopPropagation()}>
            <select autoFocus value={node.status ?? ''}
              onChange={(e) => handleStatusChange(node.id, e.target.value as Status)}
              onBlur={() => setEditingStatusId(null)}
              className="text-xs rounded px-1 py-0.5 border-0 outline-none"
              style={{ background: 'var(--glass-bg-elevated)', color: 'var(--text-primary)' }}
            >
              <option value="">—</option>
              {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </span>
        ) : (
          <span onClick={(e) => { e.stopPropagation(); setEditingStatusId(node.id); }} className="cursor-pointer" title="Click to edit status">
            {node.status ? <Badge label={node.status} color={sColor} /> : <Dim>—</Dim>}
          </span>
        );
      }
      case 'priority':
        return editingPriorityId === node.id ? (
          <span onClick={(e) => e.stopPropagation()}>
            <input autoFocus type="number" defaultValue={node.priority ?? ''}
              onBlur={(e) => handlePriorityBlur(node.id, e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              className="text-xs rounded px-1 py-0.5 border-0 outline-none w-12 text-center"
              style={{ background: 'var(--glass-bg-elevated)', color: 'var(--text-primary)' }}
            />
          </span>
        ) : (
          <span onClick={(e) => { e.stopPropagation(); setEditingPriorityId(node.id); }} className="cursor-pointer" title="Click to edit priority">
            {node.priority != null ? node.priority : <Dim>—</Dim>}
          </span>
        );
      case 'parents':
        return <RelCell nodes={getRelatedNodes(node, 'parent', nodes)} cellKey={`${node.id}-parents`} expanded={expandedRelCells.has(`${node.id}-parents`)} onToggle={toggleRelCell} onSelectNode={selectNode} />;
      case 'children':
        return <RelCell nodes={getRelatedNodes(node, 'child', nodes)} cellKey={`${node.id}-children`} expanded={expandedRelCells.has(`${node.id}-children`)} onToggle={toggleRelCell} onSelectNode={selectNode} />;
      case 'related':
        return <RelCell nodes={getRelatedNodes(node, 'peer', nodes)} cellKey={`${node.id}-related`} expanded={expandedRelCells.has(`${node.id}-related`)} onToggle={toggleRelCell} onSelectNode={selectNode} showAxis />;
      case 'bain':
        return <span className="tabular-nums">{node.attractivenessScore}</span>;
      // Financial
      case 'iRnD':
        return <span className="tabular-nums">{fmtCurrency(node.iRnD)}</span>;
      case 'fRnD':
        return <span className="tabular-nums">{fmtCurrency(node.fRnD)}</span>;
      case 'totalInv': {
        const t = (node.iRnD ?? 0) + (node.fRnD ?? 0);
        return <span className="tabular-nums font-medium" style={{ color: t > 0 ? 'var(--text-primary)' : undefined }}>{t > 0 ? fmtCurrency(t) : '—'}</span>;
      }
      case 'estRevenue':
        return <span className="tabular-nums" style={{ color: node.estRevenue != null ? '#22c55e' : undefined }}>{fmtCurrency(node.estRevenue)}</span>;
      case 'estRnD':
        return <span className="tabular-nums">{fmtCurrency(node.estRnD)}</span>;
      case 'fteAllocation': {
        const fte = fteAllocations.get(node.id) ?? node.fteAllocation;
        return <span className="tabular-nums">{fte != null ? fte.toFixed(1) : '—'}</span>;
      }
      case 'tam':
        return renderTamSamCell(node, 'tam');
      case 'sam':
        return renderTamSamCell(node, 'sam');
      // Timeline
      case 'startDate': return <span className="tabular-nums">{fmtDate(node.startDate)}</span>;
      case 'atdDate': return <span className="tabular-nums">{fmtDate(node.atdDate)}</span>;
      case 'atqDate': return <span className="tabular-nums">{fmtDate(node.atqDate)}</span>;
      case 'atsDate': return <span className="tabular-nums">{fmtDate(node.atsDate)}</span>;
      case 'mrdDate': return <span className="tabular-nums">{fmtDate(node.mrdDate)}</span>;
      // Informational
      case 'devLane':
        return <span className="truncate block max-w-[100px]">{node.devLane ?? <Dim>—</Dim>}</span>;
      case 'productManager':
        return <span className="truncate block max-w-[100px]">{node.productManager ?? <Dim>—</Dim>}</span>;
      default: return null;
    }
  }

  function renderTamSamCell(node: AdjacencyNode, field: 'tam' | 'sam'): ReactNode {
    const val = field === 'tam' ? node.tam : node.sam;
    const aiSuggested = field === 'tam' ? node.tamAiSuggested : node.samAiSuggested;
    if (editingTamSam?.nodeId === node.id && editingTamSam.field === field) {
      return (
        <span className="inline-flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <input type="number" autoFocus value={editTamSamValue}
            onChange={(e) => setEditTamSamValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commitTamSamEdit(); if (e.key === 'Escape') cancelTamSamEdit(); }}
            className="w-20 px-1 py-0.5 rounded text-right text-xs"
            style={{ background: 'var(--modal-input-bg)', color: 'var(--text-primary)', border: '1px solid rgba(59,130,246,0.5)' }}
          />
          <button onClick={commitTamSamEdit}><Check size={10} color="#22c55e" /></button>
          <button onClick={cancelTamSamEdit}><X size={10} color="#ef4444" /></button>
        </span>
      );
    }
    return (
      <span className="cursor-pointer hover:underline inline-flex items-center gap-0.5 tabular-nums"
        onClick={(e) => { e.stopPropagation(); startTamSamEdit(node.id, field, val); }}>
        {val != null ? fmtCurrency(val) : '—'}
        {aiSuggested && <Sparkles size={10} color="#d97706" />}
      </span>
    );
  }

  const detailNode = detailNodeId ? nodes.find((n) => n.id === detailNodeId) : null;

  return (
    <div ref={tableContainerRef} className="flex-1 flex flex-col min-h-0 min-w-0 relative">
      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-2">
        <label className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
          Group by:
          <select value={groupBy}
            onChange={(e) => { setGroupBy(e.target.value as GroupBy); setCollapsedGroups(new Set()); }}
            className="glass-subtle rounded px-2 py-1 text-xs border-0 outline-none"
            style={{ color: 'var(--text-primary)', background: 'var(--glass-bg-elevated)' }}
          >
            <option value="none">None</option>
            <option value="initiative">Initiative</option>
            <option value="axis">Axis</option>
          </select>
        </label>
        <div className="flex items-center gap-1.5 glass-subtle rounded px-2 py-1">
          <Search size={13} style={{ color: 'var(--text-dim)' }} />
          <input type="text" value={filters.searchText}
            onChange={(e) => setFilters({ searchText: e.target.value })}
            placeholder="Search nodes..."
            className="bg-transparent border-0 outline-none text-xs w-40"
            style={{ color: 'var(--text-primary)' }}
          />
        </div>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {filteredNodes.length} of {nodes.length} nodes
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setShowBulkEdit(true)} disabled={filteredNodes.length < 2}
            className="flex items-center gap-1 text-xs px-2.5 py-1 rounded font-medium transition-colors"
            style={{
              background: filteredNodes.length < 2 ? 'rgba(255,255,255,0.04)' : 'rgba(168,85,247,0.2)',
              color: filteredNodes.length < 2 ? '#475569' : '#c4b5fd',
              border: `1px solid ${filteredNodes.length < 2 ? 'rgba(255,255,255,0.06)' : 'rgba(168,85,247,0.3)'}`,
              cursor: filteredNodes.length < 2 ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => { if (filteredNodes.length >= 2) e.currentTarget.style.background = 'rgba(168,85,247,0.35)'; }}
            onMouseLeave={(e) => { if (filteredNodes.length >= 2) e.currentTarget.style.background = 'rgba(168,85,247,0.2)'; }}
          >
            <Rows3 size={13} /> Bulk Edit
          </button>
          <button onClick={() => void handleAddNode()}
            className="flex items-center gap-1 text-xs px-2.5 py-1 rounded font-medium transition-colors"
            style={{ background: 'rgba(59,130,246,0.2)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.3)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(59,130,246,0.35)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(59,130,246,0.2)'; }}
          >
            <Plus size={13} /> Add Node
          </button>
        </div>
      </div>

      {/* Table — absolute fill so width is fixed by parent, not by 3200px table content */}
      <div className="flex-1 relative min-h-0">
      <div className="absolute inset-0 glass-panel overflow-auto rounded-lg">
        <table className="text-xs border-collapse" style={{ color: 'var(--text-secondary)', minWidth: '3200px' }}>
          <thead>
            <tr style={{ background: 'var(--glass-bg-elevated)' }}>
              {orderedColumns.map((col) => {
                const isActive = sortKey === col.sortKey;
                const Icon = isActive ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                const isName = col.key === 'name';
                return (
                  <th key={col.key}
                    draggable
                    onDragStart={(e) => handleDragStart(e, col.key)}
                    onDragOver={(e) => handleDragOver(e, col.key)}
                    onDrop={(e) => handleDrop(e, col.key)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleSort(col.sortKey)}
                    className="px-2 py-2 font-medium cursor-pointer whitespace-nowrap select-none border-b"
                    style={{
                      borderColor: 'var(--glass-border)',
                      color: isActive ? 'var(--tab-active-color)' : 'var(--text-muted)',
                      textAlign: col.align ?? 'left',
                      opacity: draggingCol === col.key ? 0.5 : 1,
                      borderLeftWidth: dragOverCol === col.key ? 2 : undefined,
                      borderLeftColor: dragOverCol === col.key ? '#3b82f6' : undefined,
                      borderLeftStyle: dragOverCol === col.key ? 'solid' : undefined,
                      ...(isName ? {
                        position: 'sticky' as const, left: 0, zIndex: 2,
                        background: 'var(--glass-bg-elevated)',
                      } : {}),
                    }}
                  >
                    <span className="flex items-center gap-1">
                      {col.label}
                      <Icon size={12} strokeWidth={isActive ? 2.5 : 1.5} />
                    </span>
                  </th>
                );
              })}
              {/* Delete column — pinned, not draggable */}
              <th className="px-2 py-2 text-left font-medium whitespace-nowrap border-b w-10"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-muted)' }}
              />
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <GroupSection key={group.key}
                groupKey={group.key} label={group.label} nodes={group.nodes}
                isGrouped={groupBy !== 'none'}
                isCollapsed={collapsedGroups.has(group.key)}
                onToggleGroup={toggleGroup}
                groupColor={groupColor(group.key)}
                totalColSpan={totalColSpan}
                selectedNodeId={selectedNodeId}
                orderedColumns={orderedColumns}
                onRowClick={handleRowClick}
                onRowDoubleClick={handleRowDoubleClick}
                onDeleteRow={handleDeleteRow}
                renderCell={renderCell}
              />
            ))}
          </tbody>
        </table>
      </div>
      </div>

      {/* NodeModal — portals to document.body */}
      {detailNode && (
        <NodeModal node={detailNode} allNodes={nodes}
          onClose={() => setDetailNodeId(null)}
          onSave={(id, changes) => { void updateNode(id, changes); }}
          onDelete={(id) => { void deleteNode(id); setDetailNodeId(null); }}
          onDuplicate={(id) => { void duplicateNode(id); }}
          onAddParent={(parentId, childId) => { void createParentChildLink(parentId, childId); }}
          onAddChild={(parentId, childId) => { void createParentChildLink(parentId, childId); }}
          onAddRelated={(a, b) => { void createRelatedLink(a, b); }}
          onRemoveRelationship={(a, b) => { void removeRelationship(a, b); }}
          onConvertEdgeType={(a, b, toType, parentId) => { void convertEdgeType(a, b, toType, parentId); }}
        />
      )}

      {pendingConfirm && (
        <ConfirmDialog title={pendingConfirm.title} message={pendingConfirm.message}
          anchorY={pendingConfirm.anchorY}
          onConfirm={pendingConfirm.onConfirm}
          onCancel={() => setPendingConfirm(null)}
        />
      )}

      {showBulkEdit && (
        <BulkEditPanel nodes={filteredNodes} allNodes={nodes}
          onApply={(updatedNodes) => { void replaceAllNodes(updatedNodes); }}
          onClose={() => setShowBulkEdit(false)}
        />
      )}
    </div>
  );
}

// ── GroupSection ────────────────────────────────────────────────────

function GroupSection({
  groupKey, label, nodes, isGrouped, isCollapsed, onToggleGroup, groupColor,
  totalColSpan, selectedNodeId, orderedColumns, onRowClick, onRowDoubleClick,
  onDeleteRow, renderCell,
}: {
  groupKey: string; label: string; nodes: AdjacencyNode[];
  isGrouped: boolean; isCollapsed: boolean;
  onToggleGroup: (key: string) => void; groupColor: string;
  totalColSpan: number; selectedNodeId: string | null;
  orderedColumns: ColumnDef[];
  onRowClick: (id: string) => void;
  onRowDoubleClick: (id: string) => void;
  onDeleteRow: (id: string, name: string, clickY?: number) => void;
  renderCell: (col: ColumnDef, node: AdjacencyNode) => ReactNode;
}) {
  return (
    <>
      {isGrouped && (
        <tr onClick={() => onToggleGroup(groupKey)} className="cursor-pointer"
          style={{ background: groupColor + '15' }}>
          <td colSpan={totalColSpan} className="px-2 py-1.5 font-semibold text-xs" style={{ color: groupColor }}>
            <span className="flex items-center gap-1.5">
              {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              {label}
              <span className="text-[10px] font-normal" style={{ color: 'var(--text-muted)' }}>({nodes.length})</span>
            </span>
          </td>
        </tr>
      )}
      {!isCollapsed && nodes.map((node, i) => {
        const isSelected = node.id === selectedNodeId;
        const rowBg = isSelected ? 'var(--table-row-selected-bg)' : i % 2 === 0 ? 'transparent' : 'var(--table-row-alt-bg)';
        return (
          <tr key={node.id}
            onClick={() => onRowClick(node.id)}
            onDoubleClick={() => onRowDoubleClick(node.id)}
            className="cursor-pointer transition-colors border-b"
            style={{ background: rowBg, borderColor: 'var(--glass-border)' }}
            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--table-row-hover-bg)'; }}
            onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = rowBg; }}
          >
            {orderedColumns.map((col) => {
              const isName = col.key === 'name';
              return (
                <td key={col.key} className="px-2 py-1.5 whitespace-nowrap"
                  style={{
                    textAlign: col.align ?? 'left',
                    ...(isName ? {
                      position: 'sticky' as const, left: 0, zIndex: 1,
                      background: isSelected ? 'var(--table-row-selected-bg)' : i % 2 === 0 ? 'var(--table-sticky-bg)' : 'var(--table-sticky-alt-bg)',
                    } : {}),
                  }}
                >
                  {renderCell(col, node)}
                </td>
              );
            })}
            {/* Delete */}
            <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
              <button onClick={(e) => onDeleteRow(node.id, node.name, e.clientY)}
                className="p-0.5 rounded transition-opacity"
                title="Delete node"
                style={{ color: '#f87171', opacity: 0.4 }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.4'; }}
              >
                <Trash2 size={13} />
              </button>
            </td>
          </tr>
        );
      })}
    </>
  );
}

// ── Badge ────────────────────────────────────────────────────────────

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span className="inline-block text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap"
      style={{ background: color + '22', color }}>
      {label}
    </span>
  );
}

// ── Dim text ─────────────────────────────────────────────────────────

function Dim({ children }: { children: ReactNode }) {
  return <span style={{ color: 'var(--text-dim)' }}>{children}</span>;
}

// ── RelCell — relationship count badge with expandable names ─────────

function RelCell({
  nodes, cellKey, expanded, onToggle, onSelectNode, showAxis,
}: {
  nodes: AdjacencyNode[]; cellKey: string; expanded: boolean;
  onToggle: (key: string) => void; onSelectNode: (id: string | null) => void;
  showAxis?: boolean;
}) {
  if (nodes.length === 0) return <Dim>0</Dim>;
  return (
    <div>
      <span onClick={(e) => { e.stopPropagation(); onToggle(cellKey); }}
        className="inline-flex items-center gap-0.5 cursor-pointer text-[10px] px-1.5 py-0.5 rounded"
        style={{ background: 'var(--filter-pill-active-bg)', color: 'var(--hover-link-color)' }}
        title="Click to expand">
        {nodes.length}
        {expanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
      </span>
      {expanded && (
        <div className="mt-1 flex flex-col gap-0.5">
          {nodes.map((n) => (
            <span key={n.id} onClick={(e) => { e.stopPropagation(); onSelectNode(n.id); }}
              className="text-[10px] cursor-pointer hover:underline flex items-center gap-1"
              style={{ color: 'var(--hover-link-color)' }}>
              {n.name}
              {showAxis && (
                <span className="text-[9px] px-1 py-px rounded"
                  style={{ background: (AXIS_COLORS[n.adjacencyAxis] ?? '#6b7280') + '22', color: AXIS_COLORS[n.adjacencyAxis] ?? '#6b7280' }}>
                  {n.adjacencyAxis === 'New Technologies & Capabilities' ? 'New Tech' : n.adjacencyAxis}
                </span>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
